// Afiliados — DATOS REALES (GET afiliados: paginado y con filtros; el backend solo devuelve los
// de las cooperativas que cubren los roles del usuario).
//
// Ciclo del registro directo por el personal de la cooperativa (portado de Fenagro/Ecolab):
//   Nuevo afiliado → «pendiente de pago» (ocupa un cupo, no cuenta como activo)
//   → Registrar pago de inscripción → «activo» + se emite su carné (QR verificable).
// Retirar un afiliado no lo borra: pasa a «retirado» y revoca su carné.
//
// Componentes: AfiliadoWizardModal (registro), CarnetDialog (carné) y FormularioDialog (pago).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Paper,
  Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import {
  Badge as BadgeIcon, Groups as GroupsIcon, Paid as PaidIcon, Payments as PaymentsIcon, PersonAdd as PersonAddIcon,
  PersonOff as PersonOffIcon, Search as SearchIcon,
} from '@mui/icons-material';
import AfiliadoWizardModal from '../components/AfiliadoWizardModal';
import CarnetDialog from '../components/CarnetDialog';
import FormularioDialog from '../components/FormularioDialog';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { afiliacionApi, organizacionApi } from '../services/coopnetApi';
import { fecha, fmt, hoy, moneda } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });
const COLOR_ESTADO = { activo: 'success', pendiente_pago: 'warning', retirado: 'default', suspendido: 'warning' };
const ETIQUETA_ESTADO = { activo: 'Activo', pendiente_pago: 'Pendiente de pago', retirado: 'Retirado', suspendido: 'Suspendido' };
const METODOS_PAGO = [
  ['efectivo', 'Efectivo'], ['transferencia', 'Transferencia'], ['consignacion', 'Consignación'], ['pasarela', 'Pasarela de pago'], ['otro', 'Otro'],
].map(([valor, etiqueta]) => ({ valor, etiqueta }));

const Afiliados = () => {
  const { user, contexto, recargarResumen } = useAuth();
  const { cooperativas, federaciones, totales, conteos, recargar: recargarRed } = useRedReal();

  const [filtro, setFiltro] = useState({ q: '', estado: 'activo', federacionId: '', cooperativaId: '' });
  const [qDebounce, setQDebounce] = useState('');
  const [pagina, setPagina] = useState(0); // MUI: base 0
  const [tamano, setTamano] = useState(25);
  const [datos, setDatos] = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState(null); // { severidad, texto }
  const [recarga, setRecarga] = useState(0);

  const [registrando, setRegistrando] = useState(false);
  const [pagando, setPagando] = useState(null); // afiliado (fila) al que se le registra el pago
  const [verCarnet, setVerCarnet] = useState(null); // { id, nombre, apellido } | null

  // Alcance según la vista activa (además, el backend limita por los roles).
  const alcance = useMemo(() => {
    if (user?.nivel === 'cooperativa' && contexto?.cooperativa?.id) return { cooperativaId: contexto.cooperativa.id };
    if (user?.nivel === 'federacion' && contexto?.federacion?.id) return { federacionId: contexto.federacion.id };
    return {};
  }, [user?.nivel, contexto?.cooperativa?.id, contexto?.federacion?.id]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounce(filtro.q), 300);
    return () => clearTimeout(t);
  }, [filtro.q]);

  const cambiarFiltro = (parcial) => {
    setFiltro((f) => ({ ...f, ...parcial }));
    setPagina(0);
  };

  const cooperativaId = alcance.cooperativaId ?? filtro.cooperativaId;
  const federacionId = alcance.federacionId ?? filtro.federacionId;

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError('');
    organizacionApi
      .listarAfiliadosRed({ cooperativaId, federacionId, estado: filtro.estado, q: qDebounce, pagina: pagina + 1, tamano })
      .then((r) => vivo && setDatos({ total: r.total ?? 0, items: Array.isArray(r.items) ? r.items : [] }))
      .catch((e) => vivo && setError(e.message || 'No se pudieron cargar los afiliados.'))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [cooperativaId, federacionId, filtro.estado, qDebounce, pagina, tamano, recarga]);

  const refrescar = useCallback(() => {
    setRecarga((n) => n + 1);
    recargarResumen();
    recargarRed();
  }, [recargarResumen, recargarRed]);

  // Cooperativas donde se puede afiliar (dentro del alcance, activas).
  const cooperativasDisponibles = useMemo(
    () =>
      cooperativas
        .filter((k) => k.activa !== false)
        .filter((k) => !alcance.cooperativaId || k.id === alcance.cooperativaId)
        .filter((k) => !alcance.federacionId || k.federacionId === alcance.federacionId)
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [cooperativas, alcance]
  );
  const cooperativasFiltro = cooperativasDisponibles.filter((k) => !filtro.federacionId || k.federacionId === filtro.federacionId);

  // Cifras del alcance actual (del resumen real).
  const cifras = alcance.cooperativaId
    ? conteos.cooperativas.get(alcance.cooperativaId)
    : alcance.federacionId
    ? conteos.federaciones.get(alcance.federacionId)
    : { afiliados: totales.afiliados, aporteMensual: totales.aporteMensual };
  const coopPropia = alcance.cooperativaId ? cooperativas.find((k) => k.id === alcance.cooperativaId) : null;

  const stats = [
    { titulo: 'Afiliados activos', valor: fmt(cifras?.afiliados), icono: GroupsIcon, color: '#2E7D32' },
    ...(coopPropia ? [{ titulo: 'Cupo de la cooperativa', valor: `${fmt(cifras?.afiliados)} / ${fmt(coopPropia.maxAfiliados)}`, icono: GroupsIcon, color: '#E65100' }] : []),
    ...(coopPropia ? [{ titulo: 'Valor de la inscripción', valor: moneda(coopPropia.valorInscripcion), icono: PaymentsIcon, color: '#6A1B9A' }] : []),
    { titulo: 'Aporte mensual', valor: moneda(cifras?.aporteMensual), icono: PaidIcon, color: '#0D47A1' },
  ];

  const retirar = async (a) => {
    if (!window.confirm(`¿Retirar a ${a.nombre} ${a.apellido}? Queda en estado «retirado» (no se borra), libera un cupo y se revoca su carné.`)) return;
    try {
      await organizacionApi.retirarAfiliado(a.id);
      setAviso({ severidad: 'success', texto: `${a.nombre} ${a.apellido} fue retirado.` });
      refrescar();
    } catch (e) {
      setError(e.message || 'No se pudo retirar al afiliado.');
    }
  };

  const alRegistrar = (afiliado) => {
    // El nuevo afiliado queda «pendiente de pago»: se muestra ese filtro para que se vea de inmediato.
    setFiltro((f) => ({ ...f, estado: 'pendiente_pago' }));
    setPagina(0);
    setAviso({
      severidad: 'success',
      texto: `${afiliado.nombre} ${afiliado.apellido} quedó registrado como pendiente de pago (inscripción: ${moneda(afiliado.valorInscripcion)}). Registra el pago desde su fila para activarlo y emitir su carné.`,
    });
    refrescar();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Afiliados</Typography>
          <Typography variant="body1" color="text.secondary">Personas afiliadas a las cooperativas de la red</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} disabled={cooperativasDisponibles.length === 0} onClick={() => setRegistrando(true)}>
          Nuevo afiliado
        </Button>
      </Box>

      {aviso && <Alert severity={aviso.severidad} sx={{ mb: 2 }} onClose={() => setAviso(null)}>{aviso.texto}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ ...grid(220), mb: 3 }}>
        {stats.map((s) => (
          <Card key={s.titulo}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}1A`, color: s.color, display: 'flex' }}><s.icono /></Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>{s.valor}</Typography>
                <Typography variant="body2">{s.titulo}</Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          size="small" placeholder="Buscar por nombre, apellido o documento" value={filtro.q}
          onChange={(e) => cambiarFiltro({ q: e.target.value })} sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {!alcance.cooperativaId && !alcance.federacionId && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Federación</InputLabel>
            <Select label="Federación" value={filtro.federacionId} onChange={(e) => cambiarFiltro({ federacionId: e.target.value, cooperativaId: '' })}>
              <MenuItem value="">Todas</MenuItem>
              {federaciones.map((f) => <MenuItem key={f.id} value={f.id}>{f.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {!alcance.cooperativaId && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Cooperativa</InputLabel>
            <Select label="Cooperativa" value={filtro.cooperativaId} onChange={(e) => cambiarFiltro({ cooperativaId: e.target.value })}>
              <MenuItem value="">Todas</MenuItem>
              {cooperativasFiltro.map((k) => <MenuItem key={k.id} value={k.id}>{k.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={filtro.estado} onChange={(e) => cambiarFiltro({ estado: e.target.value })}>
            <MenuItem value="activo">Activos</MenuItem>
            <MenuItem value="pendiente_pago">Pendientes de pago</MenuItem>
            <MenuItem value="retirado">Retirados</MenuItem>
            <MenuItem value="suspendido">Suspendidos</MenuItem>
            <MenuItem value="">Todos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Afiliado</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Cooperativa</TableCell>
              <TableCell align="right">Aporte mensual</TableCell>
              <TableCell>Afiliado desde</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : datos.items.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay afiliados que coincidan con el filtro.</TableCell></TableRow>
            ) : (
              datos.items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{a.nombre} {a.apellido}</Typography></TableCell>
                  <TableCell>{a.tipoDocumento} {a.documento}</TableCell>
                  <TableCell>
                    {a.cooperativa}
                    <Typography variant="caption" color="text.secondary" display="block">{a.federacion}</Typography>
                  </TableCell>
                  <TableCell align="right">{moneda(a.aporteMensual)}</TableCell>
                  <TableCell>{fecha(a.fechaAfiliacion)}</TableCell>
                  <TableCell><Chip size="small" color={COLOR_ESTADO[a.estado] || 'default'} label={ETIQUETA_ESTADO[a.estado] || a.estado} /></TableCell>
                  <TableCell align="right">
                    {a.estado === 'pendiente_pago' && (
                      <Button size="small" startIcon={<PaymentsIcon />} onClick={() => setPagando(a)}>Registrar pago</Button>
                    )}
                    {a.estado === 'activo' && (
                      <Button size="small" startIcon={<BadgeIcon />} onClick={() => setVerCarnet({ id: a.id, nombre: a.nombre, apellido: a.apellido })}>Carné</Button>
                    )}
                    {(a.estado === 'activo' || a.estado === 'pendiente_pago') && (
                      <Button size="small" color="error" startIcon={<PersonOffIcon />} onClick={() => retirar(a)}>Retirar</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={datos.total} page={pagina} onPageChange={(_, p) => setPagina(p)} rowsPerPage={tamano}
          onRowsPerPageChange={(e) => { setTamano(parseInt(e.target.value, 10)); setPagina(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </TableContainer>

      <AfiliadoWizardModal
        open={registrando}
        cooperativas={cooperativasDisponibles}
        cooperativaFija={alcance.cooperativaId || ''}
        onClose={() => setRegistrando(false)}
        onRegistrado={alRegistrar}
      />

      <FormularioDialog
        abierto={Boolean(pagando)}
        titulo={`Pago de inscripción — ${pagando ? `${pagando.nombre} ${pagando.apellido}` : ''}`}
        descripcion="Al confirmar el pago el afiliado pasa a «activo» y se emite su carné."
        campos={[
          { campo: 'monto', label: 'Monto recibido', tipo: 'numero', requerido: true, positivo: true, ayuda: pagando ? `Valor de la inscripción: ${moneda(pagando.valorInscripcion)}` : undefined },
          { campo: 'metodo', label: 'Método de pago', tipo: 'select', requerido: true, opciones: METODOS_PAGO },
          { campo: 'referencia', label: 'Referencia (n.º de consignación, transferencia…)', tipo: 'texto' },
          { campo: 'fechaPago', label: 'Fecha del pago', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ monto: pagando?.valorInscripcion || '', metodo: 'efectivo', fechaPago: hoy() }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => {
          const r = await afiliacionApi.registrarPago(pagando.id, { monto: v.monto, metodo: v.metodo, referencia: v.referencia || null, fechaPago: v.fechaPago });
          const nombre = `${pagando.nombre} ${pagando.apellido}`;
          setAviso(
            r?.aviso
              ? { severidad: 'warning', texto: `Pago registrado, pero: ${r.aviso}` }
              : { severidad: 'success', texto: `Pago registrado: ${nombre} quedó activo y su carné fue emitido.` }
          );
          if (r?.carnet) setVerCarnet({ id: pagando.id, nombre: pagando.nombre, apellido: pagando.apellido });
          setFiltro((f) => ({ ...f, estado: 'activo' }));
          refrescar();
        }}
        onClose={() => setPagando(null)}
      />

      <CarnetDialog afiliado={verCarnet} onClose={() => setVerCarnet(null)} onCambio={refrescar} />
    </Box>
  );
};

export default Afiliados;
